import React from 'react';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet-async';
import {SFC} from 'types/generic';

import {AppLanguage} from 'i18n/detection';

export interface PageTitleProps {
  /** 是否给 title 追加后缀（zh：| 图片翻译专家 / en：| AI Image Translation Expert），默认 true。首页等自带完整标题的页面传 false。 */
  appendSuffix?: boolean;
  canonicalUrl?: string;
  /** 独立的 meta description；不传时沿用 ogDescription（历史行为） */
  description?: string;
  ogDescription?: string;
  ogImageAlt?: string;
  /** og:image 实际宽高，默认 1200x1200（站内默认分享图 og-image.jpg 的尺寸） */
  ogImageHeight?: number;
  ogImageUrl?: string;
  ogImageWidth?: number;
  ogTitle?: string;
  ogType?: 'website' | 'article' | 'profile' | 'book';
  ogUrl?: string;
  /** 输出 <meta name="robots" content="noindex">，用于不希望进索引的页面（如 404） */
  noindex?: boolean;
  title: string;
}

/** 中文默认 og 值（导出供测试断言引用；英文版见 defaultOgEn）。imageUrl 为站内品牌分享图（1200x1200） */
export const defaultOg = {
  description:
    '象寄翻译是一款功能强大的AI在线翻译工具，支持图片翻译、视频翻译、在线抠图、批量去水印等多种功能。我们致力于为跨境电商卖家、营销团队和个人用户提供高效、便捷的视觉内容解决方案。无论您是需要快速翻译产品图片，还是希望为您的营销视频添加多语言字幕，象寄翻译都能满足您的需求。',
  imageUrl: 'https://www.xiangjifanyi.com/home/og-image.jpg',
  type: 'website',
  url: 'https://www.xiangjifanyi.com/home/',
};

export const defaultOgEn = {
  description:
    'Xiangji AI is a powerful online AI translation tool supporting image translation, video translation, background removal, batch watermark removal and more. We provide efficient visual content solutions for cross-border e-commerce sellers, marketing teams and individual users. Whether you need to translate product images quickly or add multilingual subtitles to marketing videos, Xiangji AI has you covered.',
  imageUrl: 'https://www.xiangjifanyi.com/home/og-image.jpg',
  type: 'website',
  url: 'https://www.xiangjifanyi.com/home/',
};

const SITE_URL = 'https://www.xiangjifanyi.com/home';

/** hreflang 语言集：中文为默认路径（无前缀段），其余语言独立前缀（多语言 URL 架构，2026-10） */
const HREFLANGS: {code: string; prefix: string}[] = [
  {code: 'zh-CN', prefix: ''},
  {code: 'en', prefix: 'en'},
  {code: 'ko', prefix: 'ko'},
  {code: 'ru', prefix: 'ru'},
];

const OG_LOCALES: Record<AppLanguage, string> = {
  en: 'en_US',
  ko: 'ko_KR',
  ru: 'ru_RU',
  'zh-CN': 'zh_CN',
};

/** 站内绝对 URL（/home/ 前缀）→ 当前语言版本：/home/image-translator → /home/en/image-translator */
const localizeUrl = (url: string, language: AppLanguage) =>
  language === 'zh-CN' || !url.startsWith(`${SITE_URL}/`)
    ? url
    : url.replace(`${SITE_URL}/`, `${SITE_URL}/${language}/`);

/** 站内绝对 URL → 站内路由 path（/home/image-translator → /image-translator；/home/ → /） */
const urlToPath = (url: string) => url.replace(SITE_URL, '') || '/';

const PageTitle: SFC<PageTitleProps> = ({
  appendSuffix = true,
  canonicalUrl,
  description,
  ogDescription,
  ogImageAlt,
  ogImageHeight = 1200,
  ogImageUrl,
  ogImageWidth = 1200,
  ogTitle,
  ogType,
  ogUrl,
  noindex = false,
  title,
}) => {
  const {i18n, t} = useTranslation('common');
  const language = (i18n.language as AppLanguage) in OG_LOCALES ? (i18n.language as AppLanguage) : 'zh-CN';
  const fallbackOg = language === 'en' ? defaultOgEn : defaultOg;
  const titleToUse = appendSuffix ? `${title} | ${t('seo.titleSuffix')}` : title;
  const ogDescriptionToUse = ogDescription || fallbackOg.description;
  const ogImageAltToUse =
    ogImageAlt ||
    (language === 'zh-CN' ? '象寄翻译 — 让全球生意触手可及' : 'Xiangji AI - AI Image & Video Translation');
  const ogImageUrlToUse = ogImageUrl || fallbackOg.imageUrl;
  const ogTitleToUse = ogTitle || titleToUse;
  const ogTypeToUse = ogType || fallbackOg.type;
  const ogUrlToUse = localizeUrl(ogUrl || fallbackOg.url, language);
  const canonicalUrlToUse = canonicalUrl ? localizeUrl(canonicalUrl, language) : undefined;
  const descriptionToUse = description || ogDescriptionToUse;
  // hreflang 四语言互指 + x-default（默认中文版），基于传入的中文 canonical 推导
  const hreflangEntries = canonicalUrl
    ? HREFLANGS.map(({code, prefix}) => {
        const path = urlToPath(canonicalUrl);
        return {code, href: prefix ? `${SITE_URL}/${prefix}${path}` : `${SITE_URL}${path}`};
      })
    : [];
  return (
    <Helmet>
      <title>{titleToUse}</title>
      {canonicalUrlToUse && <link rel="canonical" href={canonicalUrlToUse} />}
      {hreflangEntries.map(({code, href}) => (
        <link href={href} hrefLang={code} key={code} rel="alternate" />
      ))}
      {canonicalUrl && <link href={`${SITE_URL}${urlToPath(canonicalUrl)}`} hrefLang="x-default" rel="alternate" />}
      {noindex && <meta name="robots" content="noindex" />}
      <meta name="description" content={descriptionToUse} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="og:description" content={ogDescriptionToUse} />
      <meta property="og:image" content={ogImageUrlToUse} />
      <meta property="og:image:alt" content={ogImageAltToUse} />
      <meta property="og:image:height" content={String(ogImageHeight)} />
      <meta property="og:image:width" content={String(ogImageWidth)} />
      <meta property="og:locale" content={OG_LOCALES[language]} />
      <meta property="og:site_name" content={language === 'zh-CN' ? '象寄翻译' : 'Xiangji AI'} />
      <meta property="og:title" content={ogTitleToUse} />
      <meta property="og:type" content={ogTypeToUse} />
      <meta property="og:url" content={ogUrlToUse} />
    </Helmet>
  );
};

export default PageTitle;
